import { injectable, inject, injectFromBase } from 'inversify'
import { makeObservable, observable, action, computed } from 'mobx'
import { AuthenticationRepository } from './authentication.repository'
import { ValidatingPresenter } from '@core/validation/validating.presenter'
import { field, z } from '@core/validation/validation.schema'
import { MIN_PASSWORD_LENGTH } from '@shared/index'
import type { FeedbackScope } from '@core/feedback/feedback.config'
import { Router } from '@core/routing/router'
import { Types } from '@core/ioc/types'
import type { RandomGateway } from '@core/services/random.gateway'

export const LoginRegisterMode = {
  LOGIN: 'login',
  REGISTER: 'register',
} as const
export type LoginRegisterMode = (typeof LoginRegisterMode)[keyof typeof LoginRegisterMode]

@injectable()
@injectFromBase()
export class LoginRegisterPagePresenter extends ValidatingPresenter {
  // Its own channel, not 'app': a login failure must stay put while the user
  // fixes the form, where an app-level message auto-dismisses like a toast.
  protected override get feedbackScope(): FeedbackScope {
    return 'auth.form'
  }

  @inject(AuthenticationRepository)
  private authenticationRepository!: AuthenticationRepository

  @inject(Router)
  private router!: Router

  @inject(Types.IRandomGateway)
  private randomGateway!: RandomGateway

  email: string = null!
  password: string = null!
  /** Register mode only — the shared code the backend gates POST /register on. */
  inviteCode: string = null!
  /**
   * Guest mode only. Starts null (unset) rather than pre-filled with the
   * generated placeholder directly — `viewModel.guestName` substitutes
   * `generatedGuestName` for display, so editing then clearing the field back
   * to nothing does not silently keep the player's first-typed name.
   */
  guestName: string | null = null
  /**
   * Computed once per page visit, not on every `viewModel` read — a fresh
   * random suggestion on every keystroke would fight the player typing over
   * it. Cleared in `setOption` should the player never actually touch it and
   * flip modes back and forth (unlikely, harmless either way).
   */
  private generatedGuestName: string | null = null
  /**
   * Signing in is the default: registering is the rarer intent, and a
   * segmented control with neither side selected is not a state the form has.
   */
  option: LoginRegisterMode = LoginRegisterMode.LOGIN

  constructor() {
    super()
    makeObservable(this, {
      email: observable,
      password: observable,
      inviteCode: observable,
      guestName: observable,
      option: observable,
      passwordAutocomplete: computed,
      submitLabel: computed,
      viewModel: computed,
      setOption: action,
      setEmail: action,
      setPassword: action,
      setInviteCode: action,
      setGuestName: action,
      login: action,
      register: action,
      enterAsGuest: action,
      logOut: action,
    })
  }

  /**
   * The password field means a different thing in each mode, and the browser
   * needs to be told which: `current-password` makes it offer the saved
   * credential, `new-password` makes it offer a generated one instead of
   * autofilling the old. Getting this wrong is what the "[DOM] Input elements
   * should have autocomplete attributes" console warning is about.
   */
  get passwordAutocomplete(): string {
    return this.option === LoginRegisterMode.REGISTER ? 'new-password' : 'current-password'
  }

  get submitLabel(): string {
    return this.i18nRepository.t(this.option === LoginRegisterMode.REGISTER ? 'auth.register' : 'auth.login')
  }

  /**
   * Every string the form renders, resolved here — the component holds no copy
   * of its own (§4.4) and no longer decides anything (§3.4/§4.3).
   */
  get viewModel() {
    return {
      logoAlt: this.i18nRepository.t('auth.logoAlt'),
      heading: this.i18nRepository.t('auth.heading'),
      subheading: this.i18nRepository.t('auth.subheading'),
      // Which mode is active is a display decision like any other (§3.4), so
      // the segmented control reads it from here rather than comparing enums
      // in the markup.
      mode: this.option,
      emailLabel: this.i18nRepository.t('auth.emailLabel'),
      passwordLabel: this.i18nRepository.t('auth.passwordLabel'),
      // `?? ''` — the fields start null, and an input bound to null is what
      // defect 21 was made of.
      email: this.email ?? '',
      password: this.password ?? '',
      // Register mode only — a render guard on this boolean, no logic in the
      // component.
      showInviteCode: this.option === LoginRegisterMode.REGISTER,
      inviteCode: this.inviteCode ?? '',
      inviteCodeLabel: this.i18nRepository.t('auth.inviteCodeLabel'),
      inviteCodePlaceholder: this.i18nRepository.t('auth.inviteCodePlaceholder'),
      loginLabel: this.i18nRepository.t('auth.login'),
      registerLabel: this.i18nRepository.t('auth.register'),
      emailPlaceholder: this.i18nRepository.t('auth.emailPlaceholder'),
      passwordPlaceholder: this.i18nRepository.t('auth.passwordPlaceholder'),
      guestLabel: this.i18nRepository.t('auth.guestButton'),
      guestNameLabel: this.i18nRepository.t('auth.guestNameLabel'),
      guestName: this.guestName ?? this.pickGeneratedGuestName(),
      submitLabel: this.submitLabel,
      passwordAutocomplete: this.passwordAutocomplete,
    }
  }

  /**
   * The form's rules, declared once. `.min(1)` rather than a hand-written
   * emptiness check: the fields start as null and only become '' after
   * reset(), and comparing against one spelling of empty is what let an
   * untouched form post empty credentials (defect 21). A schema has no such
   * blind spot — null is simply not a string.
   */
  private readonly schema = z.object({
    email: field(z.string().min(1).email(), 'auth.emailPlaceholder'),
    password: field(z.string().min(1), 'auth.passwordPlaceholder'),
  })

  // Register asks for more: an invite code, and a password long enough to be
  // worth having. Login must still accept a short legacy password.
  private readonly registerSchema = z.object({
    email: field(z.string().min(1).email(), 'auth.emailPlaceholder'),
    password: field(z.string().min(MIN_PASSWORD_LENGTH), 'auth.passwordPlaceholder'),
    inviteCode: field(z.string().min(1), 'auth.inviteCodePlaceholder'),
  })

  /** Runs the mode's schema and puts any issues on this form's own surface. */
  validateForm = () =>
    this.option === LoginRegisterMode.REGISTER
      ? this.validate(this.registerSchema, {
          email: this.email,
          password: this.password,
          inviteCode: this.inviteCode,
        })
      : this.validate(this.schema, { email: this.email, password: this.password })

  /**
   * The component used to assign `option` straight from onClick. That is a
   * write to an observable outside an action — MobX only stayed quiet about it
   * because nothing observed the field; adding the passwordAutocomplete
   * computed made it observed and the strict-mode warning appeared.
   */
  setOption = (option: LoginRegisterMode) => {
    this.option = option
  }

  setEmail = (email: string) => {
    this.email = email
  }

  setPassword = (password: string) => {
    this.password = password
  }

  setInviteCode = (inviteCode: string) => {
    this.inviteCode = inviteCode
  }

  setGuestName = (guestName: string) => {
    this.guestName = guestName
  }

  /**
   * Memoised so re-reading `viewModel` before the player has typed anything
   * keeps showing the same suggestion instead of a fresh one every render —
   * `RandomGateway` is the one call in this presenter that would otherwise
   * make it non-deterministic (invariant 8's whole point).
   */
  private pickGeneratedGuestName(): string {
    if (this.generatedGuestName === null) {
      const suffix = 1_000 + Math.floor(this.randomGateway.next() * 9_000)
      this.generatedGuestName = `${this.i18nRepository.t('auth.guestName')}${suffix}`
    }
    return this.generatedGuestName
  }

  /**
   * Which call the form makes is presenter policy, not a component branch —
   * and so is refusing to call at all. The component used to run validation
   * itself and decide whether to invoke submit().
   */
  submit = async () => {
    if (!this.validateForm().valid) return

    if (this.option === LoginRegisterMode.REGISTER) {
      await this.register()
      return
    }
    await this.login()
  }

  login = async () => {
    const loginPm = await this.authenticationRepository.login(this.email, this.password)

    this.unpackRepositoryPmToVm(loginPm, this.i18nRepository.t('auth.loggedIn'))

    if (loginPm.success) {
      const target = this.router.consumeRedirectTarget()
      await this.router.goToId(target?.routeId ?? 'Home', target?.params, target?.query)
    }
  }

  register = async () => {
    const registerPm = await this.authenticationRepository.register(this.email, this.password, this.inviteCode)

    this.unpackRepositoryPmToVm(registerPm, this.i18nRepository.t('auth.registered'))
  }

  enterAsGuest = async () => {
    const name = (this.guestName ?? this.pickGeneratedGuestName()).trim()
    const guestPm = await this.authenticationRepository.enterAsGuest(name)
    this.unpackRepositoryPmToVm(guestPm, this.i18nRepository.t('auth.guestSignedIn'))

    // Only navigate on a real session — otherwise the guard would just bounce
    // us straight back here with no explanation.
    if (guestPm.success) {
      const target = this.router.consumeRedirectTarget()
      await this.router.goToId(target?.routeId ?? 'Home', target?.params, target?.query)
    }
  }

  // Navigate first — see the note on LogoutPresenter.logout.
  logOut = async () => {
    if (!(await this.router.goToId('Login'))) return

    this.authenticationRepository.logOut()
  }
}
